import moment from "moment";
import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";

import BigContent from "veneer/BigContent";
import Box from "veneer/Box";
import VeneerGrid from "veneer/Grid";
import Stack from "veneer/Stack";
import styled from "veneer/styled";

import DevicesMap from "cortex/ui/src/fleet/view/device/component/DevicesMap";
import type {
  DeviceMetricFilter,
  DeviceNetworkTypeFilter,
  LastConnectedTier,
} from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { lastSeenDeviceCountsSelector } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import {
  ACCOUNT_STATS_LOAD_BY_DATE,
  deviceAverageUptimeSelector,
} from "cortex/ui/src/redux-store/ui-state/account-stats";
import { WidgetContainer } from "cortex/ui/src/ui/dashboard";
import DeviceMetricsPanel from "cortex/ui/src/ui/device-stats/DeviceMetricsPanel";
import LastSeenStatusPanel from "cortex/ui/src/ui/device-stats/LastSeenStatusPanel";
import { formatPercentage } from "cortex/ui/src/ui/table/formats";
import WsDsConnStatus from "cortex/ui/src/ui/ws-ds-conn-status/WsDsConnStatus";
import { Container, Panel } from "cortex/ui/src/view/layout";

import PlayerVersionTable from "./PlayerVersionTable";

const Grid = styled(VeneerGrid, {
  shouldForwardProp: (prop) => prop !== "minWidth",
})<{ minWidth?: string | number }>(({ minWidth }) => ({
  display: "flex",
  minWidth: minWidth ?? "auto",
}));

const BigContentCenter = styled(BigContent)({
  textAlign: "center",
});

export default function OverviewPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const averageUptime = useAppSelector(deviceAverageUptimeSelector);
  const lastSeenDeviceCounts = useAppSelector(lastSeenDeviceCountsSelector);
  const percent = formatPercentage(averageUptime ?? 0, 1);

  const handleTierClick = useCallback(
    (tier: LastConnectedTier, networkType?: DeviceNetworkTypeFilter) => {
      history.push("/devices", {
        lastConnected: [tier],
        networkType: networkType ?? "Production",
      });
    },
    [history],
  );

  const handleNeverConnectedClick = useCallback(() => {
    history.push("/devices", {
      lastConnected: ["Never Connected"],
    });
  }, [history]);

  const handleMetricClick = useCallback(
    (metric: DeviceMetricFilter) => {
      history.push("/devices", {
        metric,
      });
    },
    [history],
  );

  useEffect(() => {
    const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");
    void createPromiseApiAction(dispatch, ACCOUNT_STATS_LOAD_BY_DATE, [
      yesterday,
    ]);
  }, [dispatch]);

  return (
    <Container hideBox title="Overview">
      <WsDsConnStatus />
      <Stack gap={2}>
        <Grid alignItems="stretch" columns={12} container spacing={2}>
          <Grid flex={1} item xl={4} xs={5}>
            <LastSeenStatusPanel
              counts={lastSeenDeviceCounts}
              onTierClick={handleTierClick}
            />
          </Grid>
          <Grid flex={1} item xs={2}>
            <Panel mb={0}>
              <Stack
                alignItems="center"
                flex={1}
                height="100%"
                justifyContent="center"
              >
                <BigContent label="Total Devices">
                  {lastSeenDeviceCounts.totalDevices.toLocaleString()}
                </BigContent>
              </Stack>
            </Panel>
          </Grid>
          <Grid flex={1} item xs={3}>
            <DeviceMetricsPanel
              onMetricClick={handleMetricClick}
              onNeverConnectedClick={handleNeverConnectedClick}
            />
          </Grid>
          <Grid flex={1} item xs={2}>
            <Panel mb={0}>
              <Stack
                alignItems="center"
                direction="row"
                flex={1}
                height="100%"
                justifyContent="center"
              >
                <BigContentCenter
                  description="Yesterday in production"
                  label="Uptime Average"
                >
                  {percent}
                </BigContentCenter>
              </Stack>
            </Panel>
          </Grid>
        </Grid>
        <Grid alignItems="stretch" container gap={2}>
          <Grid flex={2.5} item>
            <WidgetContainer title="Device Locations">
              <Box display="flex" flex={1} px={1}>
                <DevicesMap />
              </Box>
            </WidgetContainer>
          </Grid>
          <Grid flex={1} item minWidth={375}>
            <PlayerVersionTable />
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
